/**
 * Attribution Store
 *
 * Opt-in (default OFF): inbound ad click-id capture, first-party persistence,
 * and outbound passthrough retrieval. Used by the clickIdPassthrough feature
 * in UtmLinker.js — this module owns no toggle logic of its own, only the
 * mechanics (capture / cookie / merge / URL append), all gated by the
 * `consentGranted` boolean the caller passes in.
 */

import { matchDomainPattern } from './LinkUtils.js';

/**
 * Converged set of inbound ad/attribution click-id URL params. This is the
 * cross-referenced superset used by Supabase's attribution guide, PostHog's
 * `$click_id` autocapture list, and uBlock Origin's tracking-query-param
 * filter list — do not add keys ad hoc; extend only against one of those.
 *
 * @type {string[]}
 */
export const CLICK_ID_KEYS = [
    'gclid', 'gbraid', 'wbraid', 'dclid', 'gclsrc', // Google Ads
    'gad_source',                                   // Google Ads (source signal)
    'fbclid',                                        // Meta Ads
    'msclkid',                                        // Microsoft Ads
    'yclid',                                          // Yandex Direct
    'ttclid',                                          // TikTok Ads
    'twclid',                                          // Twitter/X Ads
    'li_fat_id',                                      // LinkedIn Ads
    'epik',                                            // Pinterest Ads
    'mc_eid', 'mc_cid',                                // Mailchimp
    'igshid',                                          // Instagram
];

/** First-party cookie name used to persist captured click-ids. */
export const CLICK_ID_COOKIE_NAME = 'utm_linker_clickids';

/** Cookie lifetime, in days. */
export const CLICK_ID_COOKIE_MAX_AGE_DAYS = 90;

/**
 * Parse click-ids present in a location.search string.
 *
 * @param {string} search window.location.search (leading '?' optional)
 * @return {Object} Map of found click-id key -> value
 */
export function extractClickIdsFromSearch(search) {
    const found = {};
    if (typeof search !== 'string' || !search) return found;

    let params;
    try {
        params = new URLSearchParams(search);
    } catch (e) {
        console.warn('UTM Linker: AttributionStore URLSearchParams parse failed:', e.message);
        return found;
    }

    for (const key of CLICK_ID_KEYS) {
        const value = params.get(key);
        if (value) found[key] = value;
    }

    return found;
}

/**
 * Read a single cookie's raw value out of a `document.cookie` string.
 *
 * @param {string} cookieString
 * @param {string} name
 * @return {?string}
 */
function getCookieValue(cookieString, name) {
    if (typeof cookieString !== 'string' || !cookieString) return null;

    const parts = cookieString.split(';');
    for (const part of parts) {
        const eqIdx = part.indexOf('=');
        if (eqIdx === -1) continue;

        const key = part.slice(0, eqIdx).trim();
        if (key === name) return part.slice(eqIdx + 1).trim();
    }

    return null;
}

/**
 * Read + parse the stored click-id cookie. Tolerant of a missing cookie and
 * of malformed JSON — both resolve to an empty first/last-touch map.
 *
 * The returned object includes `_ts` (the write timestamp) when present in
 * the payload — used by captureClickIds() to detect a concurrent
 * cross-tab write between the initial read and the pre-write re-read.
 * Callers that destructure `{ first, last }` are unaffected by the extra
 * field.
 *
 * @return {{first: Object, last: Object, _ts?: number}}
 */
export function readStoredClickIds() {
    const empty = { first: {}, last: {} };

    if (typeof document === 'undefined') return empty;

    const raw = getCookieValue(document.cookie, CLICK_ID_COOKIE_NAME);
    if (!raw) return empty;

    let parsed;
    try {
        parsed = JSON.parse(decodeURIComponent(raw));
    } catch (e) {
        console.warn('UTM Linker: AttributionStore cookie JSON parse failed:', e.message);
        return empty;
    }

    if (!parsed || typeof parsed !== 'object') return empty;

    const result = {
        first: (parsed.first && typeof parsed.first === 'object') ? parsed.first : {},
        last: (parsed.last && typeof parsed.last === 'object') ? parsed.last : {},
    };

    if (typeof parsed._ts === 'number') {
        result._ts = parsed._ts;
    }

    return result;
}

/**
 * Persist the first/last-touch click-id map to the first-party cookie.
 * `Secure` is only added when the page is served over https — so this also
 * works on http dev environments.
 *
 * A `_ts` (timestamp) is included in the payload so captureClickIds() can
 * detect a concurrent cross-tab write via a timestamp mismatch between the
 * initial read and the pre-write re-read. If the caller doesn't provide a
 * `_ts`, the current time is used.
 *
 * @param {{first: Object, last: Object, _ts?: number}} data
 */
export function writeStoredClickIds(data) {
    if (typeof document === 'undefined') return;

    const payload = encodeURIComponent(JSON.stringify({
        first: (data && data.first) || {},
        last: (data && data.last) || {},
        _ts: (data && typeof data._ts === 'number') ? data._ts : Date.now(),
    }));

    const maxAge = CLICK_ID_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    const isHttps = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
    const secure = isHttps ? '; Secure' : '';

    document.cookie = `${CLICK_ID_COOKIE_NAME}=${payload}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
}

/**
 * Capture any click-ids present in the current URL into the first-party
 * cookie: first-touch is written only for a key not already stored;
 * last-touch always reflects the newest value seen for that key. No-ops when
 * consent isn't granted, when there's no window.location to read, or when the
 * current URL carries no recognized click-id.
 *
 * Cross-tab TOCTOU mitigation (bug-hunt-council HIGH #2): after the initial
 * read-merge, the stored state is RE-READ before writing. If the re-read's
 * `_ts` timestamp differs from the initial read's, a concurrent tab wrote
 * between our two reads — their first-touch entries are merged into our
 * result before writing. This narrows the race window from the full
 * read→write gap (milliseconds) down to the microsecond re-read→write gap.
 * A debug-mode `console.warn` is emitted when a concurrent write is
 * detected, so attribution data loss is diagnosable.
 *
 * @param {boolean} consentGranted Resolved consent signal (server-side + gate)
 * @param {boolean} [debugMode=false] When true, warn on concurrent-write detection
 */
export function captureClickIds(consentGranted, debugMode = false) {
    if (!consentGranted) return;
    if (typeof window === 'undefined' || !window.location) return;

    const found = extractClickIdsFromSearch(window.location.search);
    if (Object.keys(found).length === 0) return;

    // Initial read — capture the timestamp for concurrent-write detection.
    const storedInitial = readStoredClickIds();
    const first = { ...storedInitial.first };
    const last = { ...storedInitial.last };

    for (const [key, value] of Object.entries(found)) {
        if (!(key in first)) {
            first[key] = value;
        }
        last[key] = value;
    }

    // Re-read before write to narrow the TOCTOU window: if another tab
    // wrote between our initial read and this re-read, merge their
    // first-touch entries (the attribution-critical data) into our result.
    const storedBeforeWrite = readStoredClickIds();
    const initialTs     = typeof storedInitial._ts === 'number' ? storedInitial._ts : 0;
    const beforeWriteTs = typeof storedBeforeWrite._ts === 'number' ? storedBeforeWrite._ts : 0;

    if (initialTs !== beforeWriteTs) {
        for (const [key, value] of Object.entries(storedBeforeWrite.first)) {
            if (!(key in first)) first[key] = value;
        }
        for (const [key, value] of Object.entries(storedBeforeWrite.last)) {
            last[key] = value;
        }

        if (debugMode) {
            console.warn('UTM Linker: click-id store concurrent write detected during capture — merged the other tab\'s first-touch entries');
        }
    }

    writeStoredClickIds({ first, last });
}

/**
 * Merged last-touch view of the stored click-ids, for the outbound
 * passthrough step. Returns an empty object when consent isn't granted —
 * gating read access here too, not just capture, per the "gate the whole
 * feature behind consent" design.
 *
 * @param {boolean} consentGranted Resolved consent signal (server-side + gate)
 * @return {Object} { [clickIdKey]: value }
 */
export function getStoredClickIds(consentGranted) {
    if (!consentGranted) return {};

    const { first, last } = readStoredClickIds();
    return { ...first, ...last };
}

/**
 * Whether a hostname matches the click-id passthrough allowlist. An empty
 * allowlist is inert by design — click-ids are never appended to any domain
 * unless it is explicitly allowlisted.
 *
 * @param {string} hostname
 * @param {string[]} allowlist
 * @return {boolean}
 */
export function matchesClickIdAllowlist(hostname, allowlist) {
    if (!hostname || !Array.isArray(allowlist) || allowlist.length === 0) return false;
    return allowlist.some((pattern) => matchDomainPattern(hostname, pattern));
}

/**
 * Append stored click-ids to an outbound URL as query params, without
 * duplicating a key already present on the URL, preserving the URL hash.
 * Mirrors appendUtmParams()'s URL-rebuild approach in LinkUtils.js.
 *
 * @param {string} url Destination URL
 * @param {Object} clickIds Click-ids to append
 * @return {string} The URL with click-ids appended, or unchanged on failure
 */
export function appendClickIdsToUrl(url, clickIds) {
    if (typeof url !== 'string' || !clickIds || Object.keys(clickIds).length === 0) {
        return url;
    }

    try {
        const urlObj = new URL(url);
        const hash = urlObj.hash;

        Object.entries(clickIds).forEach(([key, value]) => {
            if (key && value && !urlObj.searchParams.has(key)) {
                urlObj.searchParams.set(key, value);
            }
        });

        let resultUrl = urlObj.toString();
        if (hash && !resultUrl.includes(hash)) {
            resultUrl = resultUrl.replace(/#.*$/, '') + hash;
        }

        return resultUrl;
    } catch (e) {
        return url;
    }
}
